import {
  FormEvent,
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from "react";
import {
  HiCheck,
  HiPlus,
  HiX,
  HiOutlineKey,
  HiCheckCircle,
  HiPencil,
} from "react-icons/hi";
import { FaRegCopy } from "react-icons/fa";

import { RadioGroup } from "@headlessui/react";

import { formatDistanceToNow } from "date-fns";

import useData from "../src/useData";
import useTenant, { tenantDisplayName } from "../src/useTenant";

import { AccessLevel, EnvData, Tenant, Token } from "../src/types";

import Button from "./Button";
import Modal from "./Modal";
import { prettyAccessLevel } from "../src/prettyAccessLevel";
import { useSegmentCallbackV2 } from "../src/segment";
import { useEnvironmentAction } from "../src/useAction";
import { MAX_ENVIRONMENT_TOKENS } from "@/src/constants";

interface NewToken {
  pub_id: string;
  plaintext_token: string;
  access_level: AccessLevel;
}

const CopyButton: FunctionComponent<
  JSX.IntrinsicElements["button"] & {
    text: string;
  }
> = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(() => {
    navigator.clipboard.writeText(text);
    setCopied(true);
  }, [text]);

  useEffect(() => {
    if (copied) {
      const timeout = setTimeout(() => setCopied(false), 5000);
      return () => clearTimeout(timeout);
    }
  }, [copied]);

  const i = copied ? <HiCheck /> : <FaRegCopy />;

  return (
    <Button
      medium
      icon={i}
      onClick={copy}
      theme={copied ? "default" : "primary"}
    >
      {copied ? "Copied" : "Copy"}
    </Button>
  );
};

const TokenManagementView: React.FC = () => {
  const { tenant } = useTenant();

  if (!tenant) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <h2 className="text-2xl font-space mb-2">API Keys</h2>
      <div className="text-sm mb-6">
        API Keys are used to authenticate requests to the Oso Cloud API. You can
        make as many keys as you’d like, and revoke them freely.
      </div>
      <div className="space-y-8">
        {tenant.environments.map((env) => (
          <EnvironmentTokensView tenant={tenant} env={env} key={env.pub_id} />
        ))}
      </div>
    </div>
  );
};

const EnvironmentTokensView: React.FC<{
  tenant: Tenant;
  env: EnvData;
}> = ({ tenant, env }) => {
  const { data, refresh, error } = useData<Token[]>(
    () => `/web/environment/${env.pub_id}/tokens`
  );
  const [createTokenModalOpen, setCreateTokenModalOpen] = useState(false);
  const [tokenToRevoke, setTokenToRevoke] = useState<Token | null>(null);
  const [tokenToRename, setTokenToRename] = useState<Token | null>(null);

  const createTokenAction = useEnvironmentAction("create_token", env);
  const deleteTokenAction = useEnvironmentAction("delete_token", env);
  const renameTokenAction = useEnvironmentAction("rename_token", env);

  if (error) {
    return <div>There was an error fetching your tokens.</div>;
  }

  if (!data) {
    return <div>Loading...</div>;
  }

  const atTokenLimit = data.length >= MAX_ENVIRONMENT_TOKENS;
  const tokens = data.slice().sort((a, b) => {
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  });

  return (
    <div>
      <div className="flex justify-between relative">
        <span className="font-normal text-lg text-gray-600">
          Environment: <b className="text-black">{env.name}</b>
        </span>
        <Button
          role="button"
          tabIndex={0}
          small
          className="focus:outline-2 outline-blue-500 peer"
          onClick={() => {
            setCreateTokenModalOpen(true);
          }}
          icon={<HiPlus />}
          disabled={!createTokenAction || atTokenLimit}
        >
          <span>
            Create <b>{env.name}</b> API Key
          </span>
        </Button>
        {!createTokenAction && (
          <div className="hidden peer-hover:block absolute -bottom-16 -right-0 z-10 bg-white shadow-md rounded-md p-4 text-gray-700 text-base text-left">
            You do not have permission to create new tokens for this
            environment.
          </div>
        )}
        {createTokenAction && atTokenLimit && (
          <div className="hidden peer-hover:block absolute -bottom-24 -right-0 z-10 bg-white shadow-md rounded-md p-4 text-gray-700 text-base text-left">
            You have reached the maximum number of tokens (
            {MAX_ENVIRONMENT_TOKENS}) for this environment. Please delete any
            unused ones to continue.
          </div>
        )}
      </div>
      <div className="mt-4" />
      <table className="w-full md:table-fixed">
        <thead>
          <tr className="border-b text-sm text-gray-600 pb-1">
            <td className="w-1/4">Name</td>
            <td>ID</td>
            <td>Created</td>
            <td>Last Seen</td>
            <td>Access Level</td>
            <td></td>
          </tr>
        </thead>

        <tbody>
          {tokens.map((token) => (
            <tr key={token.pub_id} className="hover:bg-gray-50">
              <td className="pr-2">
                <div className="flex space-x-3 justify-between">
                  <span className="truncate block" title={token.name ?? ""}>
                    {token.name}
                  </span>
                  {renameTokenAction && (
                    <Button
                      theme="tertiary"
                      small
                      onClick={() => setTokenToRename(token)}
                    >
                      <HiPencil />
                      <span className="sr-only">Rename</span>
                    </Button>
                  )}
                </div>
              </td>
              <td className="py-1">{token.pub_id}</td>
              <td className="text-sm">
                {formatDistanceToNow(new Date(token.created_at + "Z"), {
                  addSuffix: true,
                })}
              </td>
              <td className="text-sm">
                {token.last_seen_at
                  ? formatDistanceToNow(new Date(token.last_seen_at), {
                      addSuffix: true,
                    })
                  : "Never"}
              </td>
              <td className="text-sm">
                {prettyAccessLevel(token.access_level)}
              </td>
              <td>
                <div className="flex justify-end">
                  <Button
                    role="button"
                    xsmall
                    theme="delete"
                    icon={<HiX />}
                    onClick={setTokenToRevoke.bind(null, token)}
                    disabled={!deleteTokenAction}
                  >
                    Revoke
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {!tokens.length && (
        <div className="mt-4 flex flex-col items-center">
          <span className="text-3xl text-gray-700 mb-2">
            <HiOutlineKey />
          </span>
          <span className="text-base text-gray-600">
            No <b>{env.name}</b> API Keys
          </span>
        </div>
      )}

      <Modal
        isOpen={createTokenModalOpen}
        onClose={() => setCreateTokenModalOpen(false)}
        ignoreOutsideClick={createTokenModalOpen}
        showCloseButton
      >
        <CreateTokenModal
          tenant={tenant}
          env={env}
          refresh={refresh}
          onComplete={() => setCreateTokenModalOpen(false)}
        />
      </Modal>
      <Modal
        isOpen={tokenToRevoke !== null}
        onClose={() => setTokenToRevoke(null)}
        showCloseButton
      >
        <ConfirmRevokeModal
          tenant={tenant}
          env={env}
          token={tokenToRevoke}
          onComplete={() => (setTokenToRevoke(null), refresh())}
        />
      </Modal>
      <Modal
        isOpen={tokenToRename !== null}
        onClose={() => setTokenToRename(null)}
        showCloseButton
      >
        <RenameTokenModal
          tenant={tenant}
          env={env}
          token={tokenToRename}
          onComplete={() => (setTokenToRename(null), refresh())}
        />
      </Modal>
    </div>
  );
};

const CreateTokenModal: React.FC<{
  tenant: Tenant;
  env: EnvData;
  onComplete: () => void;
  refresh: () => void;
}> = ({ tenant, env, onComplete, refresh }) => {
  const [newToken, setNewToken] = useState<NewToken | null>(null);

  const [accessLevel, setAccessLevel] = useState<AccessLevel | null>(null);
  const [tokenName, setTokenName] = useState<string>("");

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const trackCreate = useSegmentCallbackV2().bind(null, "Create API Token");

  const createToken = useCallback(
    (access_level: AccessLevel, name: string) => {
      setError(null);
      setIsLoading(true);

      fetch(`/web/environment/${env.pub_id}/tokens`, {
        method: "POST",
        body: JSON.stringify({ access_level, name }),
      })
        .then(async (r) => {
          if (r.ok) {
            return await r.json();
          } else {
            let message = "Something went wrong- please try again.";
            try {
              const json = await r.json();
              if (json["message"]) {
                message = json["message"];
              }
            } catch (e) {
              // Some kind of weirdo error- network error, 500, etc- that we can't parse into JSON
              console.error(e);
            }

            throw message;
          }
        })
        .then((token: NewToken) => {
          // TODO: get access level from backend?
          setNewToken({ ...token, access_level });
          try {
            trackCreate({
              apiTokenAccessLevel: access_level,
              apiTokenPubId: token.pub_id,
            });
            // NOTE(gj): Don't care yet about handling Segment tracking errors.
          } catch (_) {}
          refresh();
        })
        .catch((e) => {
          setError(e);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [env, refresh, setNewToken]
  );

  function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(" ");
  }

  if (newToken) {
    return (
      <div>
        <div className="text-xl font-space">Create New API Key</div>
        <div className="mt-4" />
        <span className="text-green-700 text-lg flex space-x-2 items-center">
          <HiCheck />
          <span>
            Created <b>{prettyAccessLevel(newToken.access_level)}</b> API Key
          </span>
        </span>

        <div className="mt-4" />

        <div className="grid grid-cols-[33%,67%] gap-2">
          <div className="font-semibold">Organization</div>
          <div className="flex items-center ">
            <span>
              <span>{tenantDisplayName(tenant)}</span>
            </span>
            {tenant.kind === "sandbox" && (
              <div className="ml-2 text-xs text-white rounded font-bold bg-gray-500 uppercase py-0.5 px-1">
                developer
              </div>
            )}
            {tenant.kind === "production" && (
              <div className="ml-2 text-xs text-white rounded font-bold bg-green-700 uppercase py-0.5 px-1">
                business
              </div>
            )}
          </div>

          <div className="font-semibold">Environment</div>
          <div>{env.name}</div>

          <div className="font-semibold">Name</div>
          <div>{tokenName ?? <em>(empty)</em>}</div>

          <div className="font-semibold">ID</div>
          <div>{newToken.pub_id}</div>
        </div>

        <div className="mt-6" />
        <div>
          Save your new API Key now.{" "}
          <b className="font-semibold">
            You will not be able to access it again.
          </b>
        </div>
        <code className="border border-gray-300 rounded bg-white px-2 py-2 mt-1 text-[8pt] block overflow-auto no-scroll-bar">
          {newToken.plaintext_token}
        </code>
        <div className="mt-2" />
        <div className="flex justify-around">
          <CopyButton text={newToken.plaintext_token} />
          <Button small onClick={onComplete}>
            Dismiss
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <span className="text-xl font-space">Create New API Key</span>
      <div className="mt-6" />
      <div className="grid grid-cols-[33%,67%] gap-2">
        <div className="font-semibold">Organization</div>
        <div className="flex items-center ">
          <span>
            <span>{tenantDisplayName(tenant)}</span>
          </span>
          {tenant.kind === "sandbox" && (
            <div className="ml-2 text-xs text-white rounded font-bold bg-gray-500 uppercase py-0.5 px-1">
              developer
            </div>
          )}
          {tenant.kind === "production" && (
            <div className="ml-2 text-xs text-white rounded font-bold bg-green-700 uppercase py-0.5 px-1">
              business
            </div>
          )}
        </div>

        <div className="font-semibold">Environment</div>
        <div>{env.name}</div>
      </div>
      <div className="mt-6" />
      <div>
        <label
          htmlFor="tokenName"
          className="block text-base font-semibold leading-6 text-gray-900"
        >
          Name <span className="text-gray-600 font-normal">(Optional)</span>
        </label>
        <div className="mt-2">
          <input
            type="text"
            name="tokenName"
            id="tokenName"
            value={tokenName}
            maxLength={255}
            onChange={(e) => setTokenName(e.target.value)}
            className="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600"
          />
        </div>
        <p className="mt-2 text-sm text-gray-500" id="email-description">
          Human-readable description of this API key.
        </p>
      </div>

      <div className="mt-6" />

      <RadioGroup value={accessLevel} onChange={setAccessLevel}>
        <RadioGroup.Label className="text-base font-semibold leading-6 text-gray-900">
          Access Level
        </RadioGroup.Label>

        <div className="mt-2 grid grid-cols-2 gap-x-6">
          <RadioGroup.Option
            key={"read-only"}
            value={AccessLevel.ReadOnly}
            className={({ checked, active }) =>
              classNames(
                checked ? "border-transparent" : "border-gray-300",
                active ? "border-indigo-600 ring-2 ring-indigo-600" : "",
                "relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-sm focus:outline-none"
              )
            }
          >
            {({ checked, active }) => (
              <>
                <span className="flex flex-1">
                  <span className="flex flex-col">
                    <RadioGroup.Label
                      as="span"
                      className="block text-sm font-medium text-gray-900"
                    >
                      Read-Only
                    </RadioGroup.Label>
                    <RadioGroup.Description
                      as="span"
                      className="mt-1 flex items-center text-sm text-gray-500"
                    >
                      Grants limited access to this environment. Can only be
                      used to read facts and authorize actions, but not to write
                      facts or update policies.
                    </RadioGroup.Description>
                  </span>
                </span>
                <HiCheckCircle
                  className={classNames(
                    !checked ? "invisible" : "",
                    "h-5 w-5 text-indigo-600"
                  )}
                  aria-hidden="true"
                />
                <span
                  className={classNames(
                    active ? "border" : "border-2",
                    checked ? "border-indigo-600" : "border-transparent",
                    "pointer-events-none absolute -inset-px rounded-lg"
                  )}
                  aria-hidden="true"
                />
              </>
            )}
          </RadioGroup.Option>
          <RadioGroup.Option
            key={"read-write"}
            value={AccessLevel.ReadWrite}
            className={({ checked, active }) =>
              classNames(
                checked ? "border-transparent" : "border-gray-300",
                active ? "border-indigo-600 ring-2 ring-indigo-600" : "",
                "relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-sm focus:outline-none"
              )
            }
          >
            {({ checked, active }) => (
              <>
                <span className="flex flex-1">
                  <span className="flex flex-col">
                    <RadioGroup.Label
                      as="span"
                      className="block text-sm font-medium text-gray-900"
                    >
                      Read-Write
                    </RadioGroup.Label>
                    <RadioGroup.Description
                      as="span"
                      className="mt-1 flex items-center text-sm text-gray-500"
                    >
                      Grants total access to this environment. You will be able
                      to use this key to update a policy or to read, write, and
                      delete facts.
                    </RadioGroup.Description>
                  </span>
                </span>
                <HiCheckCircle
                  className={classNames(
                    !checked ? "invisible" : "",
                    "h-5 w-5 text-indigo-600"
                  )}
                  aria-hidden="true"
                />
                <span
                  className={classNames(
                    active ? "border" : "border-2",
                    checked ? "border-indigo-600" : "border-transparent",
                    "pointer-events-none absolute -inset-px rounded-lg"
                  )}
                  aria-hidden="true"
                />
              </>
            )}
          </RadioGroup.Option>
        </div>
      </RadioGroup>
      <div className="mt-6" />
      {error && (
        <>
          <div className="border border-red-600 rounded px-3 py-2 text-red-600">
            {error}
          </div>
          <div className="mt-6" />
        </>
      )}
      <div className="flex justify-around">
        <Button
          medium
          onClick={() => {
            onComplete();
          }}
        >
          Cancel
        </Button>
        <Button
          theme="primary"
          medium
          loading={isLoading}
          disabled={accessLevel === null || isLoading}
          onClick={() => {
            createToken(accessLevel!, tokenName);
          }}
        >
          Create
        </Button>
      </div>
    </div>
  );
};

const ConfirmRevokeModal: React.FC<{
  tenant: Tenant;
  env: EnvData;
  token: Token | null;
  onComplete: () => void;
}> = ({ tenant, env, token: tokenProp, onComplete }) => {
  // This is a little hack so that the UI doesn't flash when the modal is closing
  const [token, setToken] = useState<Token>(tokenProp as any);
  useEffect(() => {
    if (tokenProp) setToken(token);
  }, [tokenProp]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmDeleteString, setConfirmDeleteString] = useState("");
  let confirmDeleteMatches = false;

  if (confirmDeleteString === token.pub_id) {
    confirmDeleteMatches = true;
  }

  const revokeApiKey = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      if (!confirmDeleteMatches) return;
      setIsDeleting(true);
      fetch(`/web/environment/${env.pub_id}/tokens/${token.pub_id}`, {
        method: "DELETE",
      })
        .then(() => {
          setIsDeleting(false);
          onComplete();
        })
        .catch(() => {
          setIsDeleting(false);
        });
    },
    [env, onComplete, token, confirmDeleteMatches]
  );

  return (
    <>
      <span className="text-xl font-space">Revoke an API Key</span>
      <div className="mt-6" />

      <div>Are you sure you want to revoke this API key?</div>
      <div className="mt-2" />
      <div>
        <span className="font-bold text-red-700">
          If any of your apps or services are actively using this API Key,
          revoking it will cause them to stop working.
        </span>
      </div>
      <div className="mt-4" />

      <div className="grid grid-cols-[33%,67%] gap-2">
        <div className="font-semibold">Organization</div>
        <div className="flex items-center">
          <span>
            <span>{tenantDisplayName(tenant)}</span>
          </span>
          {tenant.kind === "sandbox" && (
            <div className="ml-2 text-xs text-white rounded font-bold bg-gray-500 uppercase py-0.5 px-1">
              developer
            </div>
          )}
          {tenant.kind === "production" && (
            <div className="ml-2 text-xs text-white rounded font-bold bg-green-700 uppercase py-0.5 px-1">
              business
            </div>
          )}
        </div>

        <div className="font-semibold">Environment</div>
        <div>{env.name}</div>

        <div className="font-semibold">Name</div>
        <div>{token.name ?? <em>(empty)</em>}</div>

        <div className="font-semibold">ID</div>
        <div>{token.pub_id}</div>

        <div className="font-semibold">Access Level</div>
        <div>{prettyAccessLevel(token.access_level)}</div>

        <div className="font-semibold">Created</div>
        <div>
          {formatDistanceToNow(new Date(token.created_at + "Z"), {
            addSuffix: true,
          })}
        </div>
      </div>

      <div className="mt-4" />
      <div>To continue, type the ID of the API Key, {token.pub_id}, below:</div>

      <div className="mt-2" />
      <form onSubmit={revokeApiKey}>
        <input
          className={`border py-1 px-2 text-base rounded w-full ${
            !confirmDeleteMatches && "outline-red-700 border-red-700"
          }`}
          type="text"
          value={confirmDeleteString}
          onChange={(e) => setConfirmDeleteString(e.target.value)}
          placeholder={token.pub_id}
        />
        <div className="mt-4" />
        <div className="flex space-x-2 justify-around">
          <Button
            tabIndex={0}
            medium
            onClick={(e) => {
              e.preventDefault();
              onComplete();
            }}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            tabIndex={0}
            medium
            theme="primary"
            icon={<HiX />}
            loading={isDeleting}
            disabled={!confirmDeleteMatches || isDeleting}
          >
            Revoke API Key
          </Button>
        </div>
      </form>
    </>
  );
};

const RenameTokenModal: React.FC<{
  tenant: Tenant;
  env: EnvData;
  token: Token | null;
  onComplete: () => void;
}> = ({ tenant, env, token: tokenProp, onComplete }) => {
  // This is a little hack so that the UI doesn't flash when the modal is closing
  const [token, setToken] = useState<Token>(tokenProp as any);
  useEffect(() => {
    if (tokenProp) setToken(token);
  }, [tokenProp]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState<string>(token.name ?? "");

  const renameToken = useCallback(() => {
    setIsLoading(true);

    fetch(`/web/environment/${env.pub_id}/tokens/${token.pub_id}/rename`, {
      method: "POST",
      body: newName,
    })
      .then(async (r) => {
        if (r.ok) {
          onComplete();
        } else {
          let message = "Something went wrong- please try again.";
          try {
            const json = await r.json();
            if (json["message"]) {
              message = json["message"];
            }
          } catch (e) {
            console.error(e);
          }
          throw message;
        }
      })
      .catch((e) => {
        setError(e);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [env, onComplete, token, newName]);

  return (
    <>
      <span className="text-xl font-space">Rename an API Key</span>
      <div className="mt-6" />

      <div className="grid grid-cols-[33%,67%] gap-2">
        <div className="font-semibold">Organization</div>
        <div className="flex items-center ">
          <span>
            <span>{tenantDisplayName(tenant)}</span>
          </span>
          {tenant.kind === "sandbox" && (
            <div className="ml-2 text-xs text-white rounded font-bold bg-gray-500 uppercase py-0.5 px-1">
              developer
            </div>
          )}
          {tenant.kind === "production" && (
            <div className="ml-2 text-xs text-white rounded font-bold bg-green-700 uppercase py-0.5 px-1">
              business
            </div>
          )}
        </div>

        <div className="font-semibold">Environment</div>
        <div>{env.name}</div>

        <div className="font-semibold">Name</div>
        <div>{token.name ?? <em>(empty)</em>}</div>

        <div className="font-semibold">ID</div>
        <div>{token.pub_id}</div>

        <div className="font-semibold">Access Level</div>
        <div>{prettyAccessLevel(token.access_level)}</div>

        <div className="font-semibold">Created</div>
        <div>
          {formatDistanceToNow(new Date(token.created_at + "Z"), {
            addSuffix: true,
          })}
        </div>
      </div>

      <div className="mt-6" />
      <div>
        <label
          htmlFor="tokenName"
          className="block text-base font-semibold leading-6 text-gray-900"
        >
          New Name
        </label>
        <div className="mt-2">
          <input
            type="text"
            name="tokenName"
            id="tokenName"
            value={newName}
            maxLength={255}
            onChange={(e) => {
              setNewName(e.target.value);
              setError(null);
            }}
            className="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          />
        </div>
        <p className="mt-2 text-sm text-gray-500" id="email-description">
          Human-readable description of this API key.
        </p>
      </div>

      <div className="mt-6" />
      {error && (
        <>
          <div className="border border-red-600 rounded px-3 py-2 text-red-600">
            {error}
          </div>
          <div className="mt-6" />
        </>
      )}
      <div className="flex justify-around">
        <Button
          medium
          onClick={() => {
            onComplete();
          }}
        >
          Cancel
        </Button>
        <Button
          theme="primary"
          medium
          loading={isLoading}
          disabled={isLoading}
          onClick={() => {
            renameToken();
          }}
        >
          Rename
        </Button>
      </div>
    </>
  );
};

export default TokenManagementView;
